import { API_BASE_URL, request } from "../base";

// The lead store (#1085), as Admin → Web leads sees it. Field names match the
// API row (apps/api/src/db/schema/landing-leads.ts).

export type LeadStatus =
	| "new"
	| "contacted"
	| "qualified"
	| "matched"
	| "closed"
	| "junk";

export type LeadEntryType =
	| "whatsapp"
	| "form"
	| "calculator"
	| "partner_referral";

export type LandingLead = {
	id: number;
	name: string;
	phone: string | null;
	email: string | null;
	city: string;
	locality: string | null;
	notes: string | null;
	source: string;
	pagePath: string | null;
	entryType: LeadEntryType | null;
	offer: string | null;
	utmSource: string | null;
	utmMedium: string | null;
	utmCampaign: string | null;
	utmContent: string | null;
	utmTerm: string | null;
	gclid: string | null;
	fbclid: string | null;
	priceTestArm: string | null;
	consentState: string | null;
	consentVersion: string | null;
	consentAt: string | null;
	status: LeadStatus;
	stage: string;
	qualified: "yes" | "no" | "call_to_qualify" | null;
	bhk: string | null;
	possessionMonth: string | null;
	outcome: string | null;
	adminNotes: string | null;
	dateCreated: string;
	lastUpdated: string | null;
};

export type LeadTouch = {
	id: number;
	leadId: number;
	at: string;
	entryType: LeadEntryType | null;
	channel: string | null;
	pagePath: string | null;
	utmSource: string | null;
	utmMedium: string | null;
	utmCampaign: string | null;
	ref: string | null;
	notes: string | null;
	/** The consent that came with this contact (the DPDP record). */
	consentState: string | null;
	consentVersion: string | null;
};

export type LeadFilters = {
	page?: number;
	limit?: number;
	status?: LeadStatus;
	entryType?: LeadEntryType;
	utmSource?: string;
	/** IST calendar days, `YYYY-MM-DD`. */
	from?: string;
	to?: string;
	q?: string;
	qualified?: "yes" | "no" | "call_to_qualify";
	/** Hide closed and junk. */
	open?: boolean;
};

function leadQuery(filters: LeadFilters = {}): string {
	const params = new URLSearchParams();
	for (const [key, value] of Object.entries(filters)) {
		if (value !== undefined && value !== "" && value !== false) {
			params.set(key, String(value));
		}
	}
	const qs = params.toString();
	return qs ? `?${qs}` : "";
}

export const adminLandingLeadsApi = {
	listLandingLeads(filters?: LeadFilters) {
		return request<LandingLead[]>(
			`/api/admin/landing-leads${leadQuery(filters)}`,
		);
	},

	getLandingLead(id: number) {
		return request<LandingLead & { touches: LeadTouch[] }>(
			`/api/admin/landing-leads/${id}`,
		);
	},

	updateLandingLead(
		id: number,
		patch: { status?: LeadStatus; adminNotes?: string },
	) {
		return request<LandingLead>(`/api/admin/landing-leads/${id}`, {
			method: "PATCH",
			body: patch,
		});
	},

	/**
	 * A plain link target, not a fetch: `request()` rejects non-JSON, and a
	 * top-level GET carries the same-site session cookie on its own.
	 */
	landingLeadsExportUrl(filters?: Omit<LeadFilters, "page" | "limit">) {
		return `${API_BASE_URL}/api/admin/landing-leads/export.csv${leadQuery(filters)}`;
	},
};
